import { formatDateToLocale } from "@/common/helpers/format-date";
import { CopyableValue, CopyableValueIconPosition } from "@/components/copyable-value/copyable-value";
import { SystemIdRow } from "@/components/detail-page/_components/system-id-row";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import { FingerprintIcon, HashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface SystemIdExternalIdentifier {
  id: string;
  label: string;
  value: string;
}

interface SystemIdTabProps {
  uuid: string;
  technicalId?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  identifiers?: SystemIdExternalIdentifier[];
}

export const SystemIdTab = ({ uuid, technicalId, createdAt, updatedAt, deletedAt, identifiers }: SystemIdTabProps) => {
  const { t } = useTranslation("common");

  return (
    <DetailTabGrid>
      {identifiers && (
        <DetailTabCard icon={FingerprintIcon} title={t("common:External")}>
          {identifiers.length > 0 ? (
            <div className="flex flex-col gap-1">
              {identifiers.map((identifier) => (
                <div key={identifier.id} className="flex items-center justify-between gap-4 py-1">
                  <Badge colorVariant={BadgeColorVariant.Primary} variant={BadgeVariant.Outlined}>
                    {identifier.label}
                  </Badge>
                  <span className="font-mono text-sm">
                    <CopyableValue value={identifier.value} iconPosition={CopyableValueIconPosition.Left} />
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyValue />
          )}
        </DetailTabCard>
      )}

      <DetailTabCard icon={HashIcon} title={t("common:Internal")}>
        <div className="flex flex-col gap-1">
          <SystemIdRow label={t("common:UUID")} value={uuid} mono copyable />
          {technicalId && <SystemIdRow label={t("common:TechnicalId")} value={technicalId} mono copyable />}
          <SystemIdRow label={t("common:CreatedAt")} value={createdAt ? formatDateToLocale(createdAt) : undefined} />
          <SystemIdRow label={t("common:UpdatedAt")} value={updatedAt ? formatDateToLocale(updatedAt) : undefined} />
          {deletedAt && <SystemIdRow label={t("common:Deleted")} value={formatDateToLocale(deletedAt)} />}
        </div>
      </DetailTabCard>
    </DetailTabGrid>
  );
};
